import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { CreateEmployeeRequest } from '@/lib/api/employees';

import { addEmployeeSchema, type AddEmployeeInput } from './schema';

interface Props {
  onSave: (body: CreateEmployeeRequest) => void;
  onClose: () => void;
  isSaving: boolean;
}

const DEFAULT_VALUES: AddEmployeeInput = {
  fullName: '',
  email: '',
  phone: '',
  title: '',
  hiredAt: '',
};

export function AddEmployeeModal({ onSave, onClose, isSaving }: Props) {
  const { t } = useLanguage();

  const form = useForm<AddEmployeeInput>({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(addEmployeeSchema),
  });

  function handleSubmit(data: AddEmployeeInput) {
    onSave({
      full_name: data.fullName,
      email: data.email || undefined,
      phone: data.phone || undefined,
      title: data.title || undefined,
      hired_at: data.hiredAt || undefined,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="font-semibold text-base">{t.addEmployeeTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
            data-testid="modal-close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="overflow-y-auto flex-1"
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            <Controller
              control={form.control}
              name="fullName"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="employee-name">{t.employeeFullName}</Label>
                  <Input
                    {...field}
                    id="employee-name"
                    placeholder={t.employeeFullNamePlaceholder}
                    aria-invalid={fieldState.invalid}
                    data-testid="employee-name-input"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />

            <Controller
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="employee-email">{t.employeeEmail}</Label>
                  <Input
                    {...field}
                    id="employee-email"
                    type="email"
                    placeholder={t.employeeEmailPlaceholder}
                    aria-invalid={fieldState.invalid}
                    data-testid="employee-email-input"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />

            <Controller
              control={form.control}
              name="phone"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="employee-phone">{t.employeePhone}</Label>
                  <Input
                    {...field}
                    id="employee-phone"
                    placeholder={t.phonePlaceholder}
                    aria-invalid={fieldState.invalid}
                    data-testid="employee-phone-input"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />

            <Controller
              control={form.control}
              name="title"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="employee-title">{t.employeeTitle}</Label>
                  <Input
                    {...field}
                    id="employee-title"
                    placeholder={t.employeeTitlePlaceholder}
                    aria-invalid={fieldState.invalid}
                    data-testid="employee-title-input"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />

            <Controller
              control={form.control}
              name="hiredAt"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="employee-hired-at">{t.employeeHiredAt}</Label>
                  <Input
                    {...field}
                    id="employee-hired-at"
                    type="date"
                    aria-invalid={fieldState.invalid}
                    data-testid="employee-hired-at-input"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t shrink-0">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              {t.cancel}
            </Button>
            <Button type="submit" size="sm" disabled={isSaving} data-testid="save-employee">
              {t.saveEmployee}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
